/**
 * Where an event goes, as a port.
 *
 * Two implementations exist and neither is here: a browser batches events and posts them to a
 * Worker, and a Worker writes them to Analytics Engine. What *is* here is the part both need to
 * agree on — that nothing is sent without consent, that a batch is bounded, and that a failure to
 * report is never a failure of the thing being reported on.
 *
 * **Telemetry that can break the product is worse than no telemetry.** Every path here swallows
 * its own errors. A counter that threw would turn "we could not measure this" into "this did not
 * work", which is the opposite of what measuring is for.
 *
 * **Batched, because an event is not worth a request.** A funnel step is a few bytes and there are
 * a handful per session; sending each one immediately would put a network round trip in the middle
 * of somebody creating a map. The flush happens on a timer and when the page goes away — which is
 * the only moment that matters, because a session that is never flushed is a session that never
 * happened as far as the funnel is concerned.
 */
import { mayCount } from './consent';
import { MAX_TIMING_MS } from './events';
/** How many events one batch may carry. A session produces a handful; this is a bound, not a size. */
export const MAX_BATCH = 50;
/** How long a batch waits before it goes. Long enough to collect a session's worth of steps. */
const FLUSH_AFTER_MS = 10_000;
/**
 * An event, as it is allowed to leave.
 *
 * A timing is rounded and clamped: a microsecond is not a number anybody acts on, and a
 * five-minute "timing" is a hang rather than a duration — recording it as one would move an
 * average nobody could then trust.
 */
function normalise(event) {
    if (event.kind !== 'timing')
        return event;
    const ms = Math.min(Math.max(0, Math.round(event.ms)), MAX_TIMING_MS);
    return { ...event, ms };
}
export function createTelemetry(input) {
    const flushAfterMs = input.flushAfterMs ?? FLUSH_AFTER_MS;
    let waiting = [];
    let cancel = null;
    let stopped = false;
    const isStopped = () => stopped;
    async function flush() {
        if (cancel !== null) {
            cancel();
            cancel = null;
        }
        if (waiting.length === 0)
            return;
        // Taken before the await: an event counted while a batch is in flight belongs to the next
        // batch, not to this one, and must not be lost if this one fails.
        const batch = waiting;
        waiting = [];
        try {
            await input.transport.send(batch);
        }
        catch {
            // Deliberately swallowed — see the header. A counter that threw would turn "we could not
            // measure this" into "this did not work".
        }
    }
    return {
        count: (event) => {
            if (isStopped() || !mayCount(input.environment.consent()))
                return;
            // Dropped rather than trimmed at the front: the first events of a session are the funnel,
            // and a session that produced fifty of these has something wrong with it that a counter
            // should not paper over.
            if (waiting.length >= MAX_BATCH)
                return;
            waiting.push(normalise(event));
            cancel ??= input.environment.schedule(() => {
                cancel = null;
                void flush();
            }, flushAfterMs);
        },
        flush,
        stop: () => {
            stopped = true;
            if (cancel !== null) {
                cancel();
                cancel = null;
            }
            waiting = [];
        },
    };
}
//# sourceMappingURL=sink.js.map